import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import type {
  Comparator,
  Condition,
  ConditionGroup,
  SimpleCondition,
} from "@/types";

import {
  type DatePresetComparator,
  type Filter,
  isDatePreset,
} from "./FilterSchema";

dayjs.extend(utc);

export interface FilterToConditionOptions {
  /**
   * Outer wrap operator when there are 2+ filter rows. Defaults to `"AND"`
   * (every filter must match). Pass `"OR"` to join with at-least-one
   * semantics. Single-filter and zero-filter cases ignore this option.
   */
  matchMode?: "AND" | "OR";
}

/**
 * Convert the component's `Filter[]` output into a wire-level `Condition`.
 *
 * Empty input → `undefined` (callers omit the `condition` field on
 * `SearchQueryBody` entirely). Multiple filter rows are wrapped in an outer
 * group whose operator is governed by `opts.matchMode` (default `"AND"`).
 * Per-row expansion handles UI-only comparators and multi-value fan-out:
 *
 *   - `BETWEEN [a, b]` → AND of `GREATER_THAN_OR_EQUAL a` + `LESS_THAN_OR_EQUAL b`
 *     (exact instants; inclusive on both edges)
 *   - Date relative presets (TODAY / LAST_N_DAYS / THIS_MONTH) → AND of
 *     GTE+LTE bracketing the `now`-relative window, resolved at call time
 *     so a saved filter stays live across days.
 *   - Multi-value with a positive op (EQUALS/CONTAINS/LIKE) → OR group
 *   - Multi-value with a negative op (NOT_EQUALS/NOT_CONTAINS/NOT_LIKE) → AND group (set complement)
 *   - IS_NULL / IS_NOT_NULL (enum, boolean) → SimpleCondition with empty
 *     `value` (the wire comparator carries the semantic; `value` is required
 *     by the schema but ignored by the backend for null-checks)
 *
 * The helper assumes valid input — empty values (other than the explicitly
 * empty-allowed IS_NULL / IS_NOT_NULL checks and the valueless date presets)
 * and malformed BETWEEN ranges are rejected upstream by the per-type value
 * inputs' validity checks. Don't call this with arbitrary user-typed text.
 */
export function filterToCondition(
  filters: Filter[],
  opts: FilterToConditionOptions = {},
): Condition | undefined {
  if (filters.length === 0) {
    return undefined;
  }
  if (filters.length === 1) {
    return filterRowToCondition(filters[0]);
  }
  return {
    operator: opts.matchMode ?? "AND",
    conditions: filters.map(filterRowToCondition),
  };
}

const NEGATIVE_MULTI_VALUE_COMPARATORS = new Set<string>([
  "NOT_EQUALS",
  "NOT_CONTAINS",
  "NOT_LIKE",
]);

const NULL_CHECK_COMPARATORS = new Set<string>(["IS_NULL", "IS_NOT_NULL"]);

function filterRowToCondition(filter: Filter): Condition {
  const { comparator } = filter;
  // Option-backed filters store labeled options; only the wire `value`
  // crosses this boundary — the display label never reaches a `Condition`.
  const values: readonly (string | number | boolean)[] =
    filter.type === "set" || filter.type === "enum"
      ? filter.values.map((option) => option.value)
      : filter.values;
  // A metric filter carries the bare dotted path in the UI; the backend's
  // session query convention addresses it as `metric.<name>`, so prepend the
  // prefix at the wire boundary. Everything downstream (single/multi-value
  // fan-out) is identical to numeric.
  const field =
    filter.type === "metric" ? `metric.${filter.field}` : filter.field;

  // IS_NULL / IS_NOT_NULL carry their semantic on the comparator; the schema
  // requires a value, but the backend ignores it for null checks.
  if (NULL_CHECK_COMPARATORS.has(comparator)) {
    return { field, comparator: comparator as Comparator, value: "" };
  }

  // UI-only date comparators expand here.
  if (filter.type === "date") {
    if (isDatePreset(filter.comparator)) {
      return datePresetToCondition(field, filter.comparator);
    }
    if (filter.comparator === "BETWEEN") {
      // The datetime input captures exact instants, so both bounds pass
      // through verbatim: inclusive on both edges (GTE lower, LTE upper).
      return {
        operator: "AND",
        conditions: [
          simpleCondition(field, "GREATER_THAN_OR_EQUAL", filter.values[0]),
          simpleCondition(field, "LESS_THAN_OR_EQUAL", filter.values[1]),
        ],
      };
    }
  }

  // Single-value path covers every non-multi operator (numeric ranges,
  // boolean EQUALS, date EQUALS, single-string EQUALS, etc.).
  if (values.length <= 1) {
    return simpleCondition(field, comparator as Comparator, values[0]);
  }

  // Multi-value: positive ops fan out to OR, negative ops to AND.
  const operator: ConditionGroup["operator"] =
    NEGATIVE_MULTI_VALUE_COMPARATORS.has(comparator) ? "AND" : "OR";
  return {
    operator,
    conditions: values.map((value) =>
      simpleCondition(field, comparator as Comparator, value),
    ),
  };
}

function simpleCondition(
  field: string,
  comparator: Comparator,
  value: boolean | number | string,
): SimpleCondition {
  return { field, comparator, value };
}

/**
 * Resolve a date preset to a concrete AND-of-GTE-LTE range, anchored to
 * `now`. Lives here (not at filter-creation time inside the value input)
 * so a saved or shared filter named "Last 7 days" still means *the last
 * seven days* the next time the query runs.
 *
 * UTC-anchored to match the date input's own UTC-midnight convention; the
 * upper bound is end-of-day / end-of-month so the window is inclusive on
 * both edges.
 */
function datePresetToCondition(
  field: string,
  comparator: DatePresetComparator,
): ConditionGroup {
  const now = dayjs.utc();
  let start: dayjs.Dayjs;
  let end: dayjs.Dayjs;
  switch (comparator) {
    case "TODAY":
      start = now.startOf("day");
      end = now.endOf("day");
      break;
    case "LAST_7_DAYS":
      start = now.subtract(7, "day").startOf("day");
      end = now.endOf("day");
      break;
    case "LAST_30_DAYS":
      start = now.subtract(30, "day").startOf("day");
      end = now.endOf("day");
      break;
    case "LAST_90_DAYS":
      start = now.subtract(90, "day").startOf("day");
      end = now.endOf("day");
      break;
    case "THIS_MONTH":
      start = now.startOf("month");
      end = now.endOf("month");
      break;
    default: {
      // Exhaustiveness guard — if a new preset lands in `DatePresetComparator`
      // but isn't handled above, this assignment fails to compile.
      const exhaustive: never = comparator;
      throw new Error(`Unhandled date preset: ${String(exhaustive)}`);
    }
  }
  return {
    operator: "AND",
    conditions: [
      simpleCondition(field, "GREATER_THAN_OR_EQUAL", start.toISOString()),
      simpleCondition(field, "LESS_THAN_OR_EQUAL", end.toISOString()),
    ],
  };
}
